import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { AlertCircle, CheckCircle, Clock, Loader2, Mail, XCircle } from 'lucide-react';

import { waitlistService } from '@/services/api/waitlist.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguagePill } from '@/components/chrome/LanguagePill';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function GuestWaitlistPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation(['waitlist', 'common', 'publicBooking']);
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: entry, isLoading, error } = useQuery({
    queryKey: ['guest-waitlist', token],
    queryFn: () => waitlistService.getByToken(token!),
    enabled: !!token,
    retry: false,
  });

  const leaveMutation = useMutation({
    mutationFn: () => waitlistService.leaveByToken(token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-waitlist', token] });
      setConfirmOpen(false);
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-soft">{t('common:loading', 'Loading...')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <div>
                <h3 className="text-h3 text-ink">{t('waitlist:guest.notFoundTitle', 'Waitlist entry not found')}</h3>
                <p className="text-soft">{t('waitlist:guest.notFoundDescription', "We couldn't find this waitlist entry. The link may have expired.")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const startTime = parseISO(entry.slot.start_time);
  const isWaiting = entry.status === 'waiting';
  const isPromoted = entry.status === 'promoted';
  const isCancelled = entry.status === 'cancelled';
  const isExpired = entry.status === 'expired';
  const position = entry.position ?? 0;
  const ahead = Math.max(0, position - 1);

  const statusBadge = () => {
    if (isWaiting) return <Badge variant="default">{t('waitlist:guest.status.waiting', 'On waitlist')}</Badge>;
    if (isPromoted) return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{t('waitlist:guest.status.promoted', 'Promoted')}</Badge>;
    if (isCancelled) return <Badge variant="secondary">{t('waitlist:guest.status.cancelled', 'Cancelled')}</Badge>;
    if (isExpired) return <Badge variant="secondary">{t('waitlist:guest.status.expired', 'Expired')}</Badge>;
    return null;
  };

  const heroIcon = isPromoted ? (
    <CheckCircle className="h-14 w-14 mb-3 text-success" />
  ) : isCancelled || isExpired ? (
    <XCircle className="h-14 w-14 mb-3 text-soft" />
  ) : (
    <Clock className="h-14 w-14 mb-3 text-violet-500" />
  );

  const heroTitle = isPromoted
    ? t('waitlist:guest.promotedBanner.title', 'A spot opened up!')
    : isCancelled || isExpired
      ? t('waitlist:guest.cancelledBanner.title', 'No longer on this waitlist')
      : t('waitlist:guest.title', 'Waitlist');

  const heroSubtitle = isPromoted
    ? t('waitlist:guest.promotedBanner.body', 'Check your email for the booking confirmation.')
    : isCancelled || isExpired
      ? t('waitlist:guest.cancelledBanner.body', 'You have been removed from this waitlist.')
      : ahead === 0
        ? t('waitlist:guest.heroWaitingFirst', "You're first in line — we'll notify you as soon as a spot opens up.")
        : t('waitlist:guest.heroWaiting', "{{count}} person ahead of you. We'll notify you as soon as a spot opens up.", { count: ahead });

  return (
    <div className="min-h-screen bg-surface-2 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex justify-end">
          <LanguagePill />
        </div>

        <Card className="shadow-sm">
          <CardContent className="p-6 sm:p-8 space-y-6">
            {/* Hero */}
            <div className="flex flex-col items-center text-center">
              {heroIcon}
              <h2 className="text-h2 text-ink mb-1">{heroTitle}</h2>
              {isWaiting && (
                <p className="text-h3 text-violet-600 mb-1">
                  {t('waitlist:guest.positionLabel', 'Position #{{position}}', { position })}
                </p>
              )}
              <p className="text-body-sm text-soft max-w-md">{heroSubtitle}</p>
            </div>

            {/* Details */}
            <div className="rounded-2xl border border-line bg-surface-2">
              <header className="px-5 py-3 border-b border-line flex items-center justify-between">
                <h3 className="text-body font-semibold text-ink">
                  {t('waitlist:guest.detailsTitle', 'Class details')}
                </h3>
                {statusBadge()}
              </header>
              <div className="flex flex-col divide-y divide-line px-5">
                <SummaryRow
                  label={t('publicBooking:confirmation.service', 'Service')}
                  value={entry.service.name}
                />
                <SummaryRow
                  label={t('waitlist:guest.date', 'Date')}
                  value={format(startTime, getDateFormats().full, { locale: getDateLocale() })}
                />
                <SummaryRow
                  label={t('waitlist:guest.time', 'Time')}
                  value={format(startTime, 'HH:mm')}
                />
                <SummaryRow
                  label={t('waitlist:guest.provider', 'Provider')}
                  value={entry.provider.display_name}
                />
              </div>
            </div>

            {/* Email note */}
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium text-ink">
                  {isPromoted
                    ? t('waitlist:guest.promotedBanner.title', 'A spot opened up!')
                    : t('publicBooking:confirmation.waitlist.emailNote', "We'll email you when a spot opens up.")}
                </p>
                <p className="text-body-sm text-soft mt-1">
                  {t('publicBooking:confirmation.checkSpam', "If you don't see it, please check your spam folder.")}
                </p>
              </AlertDescription>
            </Alert>

            {/* Leave action */}
            {isWaiting && (
              <div className="space-y-3">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setConfirmOpen(true)}
                  disabled={leaveMutation.isPending}
                >
                  {leaveMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('waitlist:guest.leaving', 'Leaving...')}
                    </>
                  ) : (
                    t('waitlist:guest.leaveButton', 'Leave waitlist')
                  )}
                </Button>
                {leaveMutation.isError && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      {t('waitlist:guest.leaveError', "Couldn't leave the waitlist. Please try again.")}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('waitlist:guest.leaveConfirmTitle', 'Leave the waitlist?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('waitlist:guest.leaveConfirmDescription', "You'll lose your spot and won't be notified if one opens up. This cannot be undone.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={leaveMutation.isPending}>
              {t('common:actions.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); leaveMutation.mutate(); }}
              disabled={leaveMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('waitlist:guest.leaveButton', 'Leave waitlist')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-body-sm text-soft">{label}</span>
      <span className="text-body-sm font-medium text-ink text-right">{value}</span>
    </div>
  );
}
